import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  CheckCircle2, AlertTriangle, XCircle, Shield, Building2, Loader2,
  ChevronDown, ChevronUp, MapPin, Filter, X, TrendingUp,
  AlertOctagon, Info, Clock, Users, ArrowRightLeft, Calendar, Activity
} from 'lucide-react';
import AitPortfolioOperationalMetrics from './AitPortfolioOperationalMetrics';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend,
  BarChart, Bar, Cell, PieChart, Pie, AreaChart, Area
} from 'recharts';

const COMPLIANCE_COLORS = {
  compliant: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle2, color: '#22c55e' },
  at_risk: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: AlertTriangle, color: '#ea580c' },
  breach: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle, color: '#ef4444' },
  fixed_set_aside: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: Shield, color: '#3b82f6' },
  no_data: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-500 dark:text-gray-400', icon: Building2, color: '#6b7280' },
};

const STATUS_LABELS = {
  compliant: 'Compliant', at_risk: 'At Risk', breach: 'Breach',
  fixed_set_aside: 'Fixed Set-Aside', no_data: 'No Data',
};

const FLEXIBILITY_COLORS = {
  high: 'text-green-600 dark:text-green-400',
  moderate: 'text-blue-600 dark:text-blue-400',
  constrained: 'text-orange-600 dark:text-orange-400',
  minimal: 'text-orange-600 dark:text-orange-400',
  none: 'text-red-600 dark:text-red-400',
};

const PRESSURE_COLORS = {
  low: 'text-green-600 dark:text-green-400',
  moderate: 'text-blue-600 dark:text-blue-400',
  elevated: 'text-orange-600 dark:text-orange-400',
  critical: 'text-orange-600 dark:text-orange-400',
  breach: 'text-red-600 dark:text-red-400',
};

const SEVERITY_ICONS = {
  critical: { icon: AlertOctagon, color: 'text-red-500' },
  warning: { icon: AlertTriangle, color: 'text-orange-500' },
  info: { icon: Info, color: 'text-blue-500' },
};

// Maximally distinct hues — no two adjacent colors look similar
const LINE_COLORS = [
  '#2563eb', // blue
  '#dc2626', // red
  '#16a34a', // green
  '#9333ea', // purple
  '#ea580c', // orange
  '#0891b2', // cyan
  '#db2777', // pink
  '#ca8a04', // gold
  '#4f46e5', // indigo
  '#059669', // emerald
  '#c026d3', // fuchsia
  '#0d9488', // teal
  '#e11d48', // rose
  '#65a30d', // lime
  '#7c3aed', // violet
  '#334155', // slate
  '#0284c7', // sky
  '#be123c', // crimson
  '#15803d', // dark green
  '#a21caf', // magenta
];

export default function AitPortfolioAnalytics() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    portfolioData, portfolioLoading, fetchPortfolioAnalytics,
    fetchPortfolioTrends, fetchOperationalMetrics, updatePortfolioPreference, properties, fetchProperties
  } = useAit();

  const [trends, setTrends] = useState([]);
  const [trendMonths, setTrendMonths] = useState(12);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState({ critical: true, warning: true, info: false });
  const [alertsPage, setAlertsPage] = useState(1);
  const ALERTS_PER_PAGE = 8;
  const [showFilter, setShowFilter] = useState(false);
  const [opsMetrics, setOpsMetrics] = useState(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsMonths, setOpsMonths] = useState(12);
  const [portEventFilter, setPortEventFilter] = useState('all');

  const isDark = theme === 'dark';

  useEffect(() => {
    fetchPortfolioAnalytics();
    if (!properties.length) fetchProperties();
  }, [fetchPortfolioAnalytics, fetchProperties, properties.length]);

  useEffect(() => {
    setOpsLoading(true);
    fetchOperationalMetrics(opsMonths).then(data => {
      setOpsMetrics(data);
      setOpsLoading(false);
    });
  }, [opsMonths, fetchOperationalMetrics]);

  const [trendsKey, setTrendsKey] = useState(0);

  useEffect(() => {
    setTrendsLoading(true);
    fetchPortfolioTrends(trendMonths).then(data => {
      setTrends(data || []);
      setTrendsLoading(false);
    });
  }, [trendMonths, trendsKey, fetchPortfolioTrends]);

  const summary = portfolioData?.summary;
  const cards = portfolioData?.property_cards || [];
  const alerts = portfolioData?.alerts || [];

  const alertsByGroup = useMemo(() => {
    const groups = { critical: [], warning: [], info: [] };
    alerts.forEach(a => {
      const key = typeof a.severity === 'string' ? a.severity : 'info';
      (groups[key] || groups.info).push(a);
    });
    return groups;
  }, [alerts]);

  // Build chart data from trends
  const chartData = useMemo(() => {
    if (!trends.length) return [];
    const allDates = new Set();
    trends.forEach(t => t.snapshots?.forEach(s => allDates.add(s.date)));
    const sorted = [...allDates].sort();
    return sorted.map(date => {
      const point = { date };
      trends.forEach(t => {
        const snap = t.snapshots?.find(s => s.date === date);
        if (snap?.average_income != null) {
          point[t.property_name] = snap.average_income;
        }
      });
      return point;
    });
  }, [trends]);

  const refreshAll = () => {
    fetchPortfolioAnalytics();
    setTrendsKey(k => k + 1);
  };

  const handleToggleExclusion = async (propertyId, currentlyIncluded) => {
    await updatePortfolioPreference(propertyId, currentlyIncluded);
    refreshAll();
  };

  if (portfolioLoading && !portfolioData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className={`text-center py-16 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <TrendingUp size={40} className={isDark ? 'text-gray-600 mx-auto mb-3' : 'text-gray-300 mx-auto mb-3'} />
        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Unable to load analytics data.</p>
      </div>
    );
  }

  const includedIds = new Set(cards.map(c => c.id));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(COMPLIANCE_COLORS).map(([key, style]) => {
          const count = summary?.[key] || 0;
          const pct = summary?.total_properties > 0 ? Math.round(count / summary.total_properties * 100) : 0;
          const Icon = style.icon;
          return (
            <div
              key={key}
              className={`p-4 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className={style.text.split(' ')[0]} />
                <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {STATUS_LABELS[key]}
                </span>
              </div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">{count}</div>
              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{pct}% of portfolio</div>
            </div>
          );
        })}
      </div>

      {/* Portfolio Stats */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between mb-3">
          <span className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Portfolio Overview</span>
          <button
            onClick={() => setShowFilter(true)}
            className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline text-xs"
          >
            <Filter size={12} /> Filter Properties
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-3 text-center">
          {[
            { label: 'Properties', value: summary?.total_properties || 0 },
            { label: 'Units', value: summary?.total_units || 0 },
            { label: 'Occupied', value: summary?.occupied || 0, color: 'text-green-500' },
            { label: 'Leased', value: summary?.leased || 0, color: 'text-blue-500' },
            { label: 'Notice', value: summary?.notice || 0, color: (summary?.notice || 0) > 0 ? 'text-orange-500' : '' },
            { label: 'Vacant', value: summary?.vacant || 0, color: summary?.vacant > 0 ? 'text-orange-500' : '' },
            { label: 'Occupancy', value: `${summary?.occupancy_percentage || 0}%` },
            { label: 'Over-Income', value: summary?.over_income || 0, color: summary?.over_income > 0 ? 'text-red-500' : '' },
            { label: 'Recert Overdue', value: summary?.recert_overdue || 0, color: summary?.recert_overdue > 0 ? 'text-red-500' : '' },
            { label: 'Recert 30d', value: summary?.recert_due_30d || 0, color: summary?.recert_due_30d > 0 ? 'text-orange-500' : '' },
            { label: 'Leases 90d', value: summary?.expiring_leases_90d || 0, color: summary?.expiring_leases_90d > 0 ? 'text-orange-500' : '' },
          ].map(s => (
            <div key={s.label}>
              <p className={`text-lg font-semibold ${s.color || ''}`}>{s.value}</p>
              <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row — Compliance Distribution + Headroom Comparison */}
      {cards.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Compliance Distribution Donut */}
          <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Compliance Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={Object.entries(COMPLIANCE_COLORS)
                    .map(([key, style]) => ({ name: STATUS_LABELS[key], value: summary?.[key] || 0, fill: style.color }))
                    .filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {Object.entries(COMPLIANCE_COLORS)
                    .map(([key, style]) => ({ value: summary?.[key] || 0, fill: style.color }))
                    .filter(d => d.value > 0)
                    .map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f2937' : '#fff',
                    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                    borderRadius: 8, fontSize: 12
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Headroom Comparison Bar */}
          <div className={`rounded-xl border p-4 flex flex-col ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Headroom by Property</h3>
            {(() => {
              const headroomData = cards
                .filter(c => c.headroom != null)
                .map(c => ({ name: c.name.length > 15 ? c.name.slice(0, 15) + '...' : c.name, headroom: c.headroom, fullName: c.name }))
                .sort((a, b) => a.headroom - b.headroom);
              if (!headroomData.length) return (
                <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No headroom data available.</div>
              );
              const chartHeight = Math.max(120, headroomData.length * 28 + 30);
              const needsScroll = chartHeight > 200;
              return (
                <div className={needsScroll ? 'overflow-y-auto flex-1' : ''} style={needsScroll ? { maxHeight: 200 } : undefined}>
                  <div style={{ height: chartHeight }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={headroomData} layout="vertical" margin={{ left: 0, right: 20, top: 1, bottom: 1 }} barCategoryGap="8%">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} horizontal={false} />
                        <XAxis type="number" domain={[-5, 'auto']} tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={120}
                          tick={{ fontSize: 10, fill: isDark ? '#9ca3af' : '#6b7280' }}
                          interval={0}
                        />
                        <Tooltip
                          formatter={(val) => [`${val}%`, 'Headroom']}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                          contentStyle={{
                            backgroundColor: isDark ? '#1f2937' : '#fff',
                            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                            borderRadius: 8, fontSize: 12
                          }}
                        />
                        <ReferenceLine x={0} stroke="#ef4444" strokeDasharray="3 3" />
                        <Bar dataKey="headroom" radius={[0, 4, 4, 0]} barSize={10}>
                          {headroomData.map((entry, i) => (
                            <Cell key={i} fill={entry.headroom < 0 ? '#ef4444' : entry.headroom < 2 ? '#ea580c' : entry.headroom < 5 ? '#3b82f6' : '#22c55e'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Historical Trends Line Chart */}
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-500" />
            Average Income Trends
          </h3>
          <div className="flex gap-1">
            {[3, 6, 12].map(m => (
              <button
                key={m}
                onClick={() => setTrendMonths(m)}
                className={`px-2 py-1 rounded text-xs ${
                  trendMonths === m
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {m}mo
              </button>
            ))}
          </div>
        </div>
        {trendsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-blue-500" size={24} />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }} />
              <YAxis domain={[0, 80]} tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? '#1f2937' : '#fff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  borderRadius: 8,
                  fontSize: 12
                }}
              />
              <ReferenceLine y={60} stroke="#ef4444" strokeDasharray="5 5" label={{ value: '60% Limit', fill: '#ef4444', fontSize: 10 }} />
              {trends.map((t, i) => (
                <Line
                  key={t.property_id}
                  type="monotone"
                  dataKey={t.property_name}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className={`text-center py-12 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Clock size={24} className="mx-auto mb-2 opacity-50" />
            No snapshot data yet. Save snapshots from building views to see trends here.
          </div>
        )}
      </div>

      {/* Alerts Panel — fixed height with scroll + pagination */}
      {alerts.length > 0 && (() => {
        const totalPages = Math.ceil(alerts.length / ALERTS_PER_PAGE);
        const paged = alerts.slice((alertsPage - 1) * ALERTS_PER_PAGE, alertsPage * ALERTS_PER_PAGE);
        return (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle size={14} className="text-orange-500" />
                Alerts ({alerts.length})
              </h3>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                {['critical', 'warning', 'info'].map(sev => {
                  const count = alertsByGroup[sev].length;
                  if (!count) return null;
                  const cfg = SEVERITY_ICONS[sev];
                  const Icon = cfg.icon;
                  return (
                    <span key={sev} className={`inline-flex items-center gap-0.5 ${cfg.color}`}>
                      <Icon size={10} /> {count}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 256 }}>
              <div className="px-4 py-2 space-y-1">
                {paged.map((a, i) => {
                  const sevCfg = SEVERITY_ICONS[a.severity] || SEVERITY_ICONS.info;
                  const SevIcon = sevCfg.icon;
                  return (
                    <button
                      key={(alertsPage - 1) * ALERTS_PER_PAGE + i}
                      onClick={() => navigate(`/dashboard/ait/${a.property_id}`)}
                      className={`w-full text-left flex items-start gap-2 px-3 py-2 rounded text-xs hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                    >
                      <SevIcon size={12} className={`${sevCfg.color} shrink-0 mt-0.5`} />
                      <span>
                        <span className="font-medium text-gray-900 dark:text-white">{a.property_name}</span>
                        <span className="mx-1">-</span>
                        {a.detail}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Page {alertsPage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setAlertsPage(p => Math.max(1, p - 1))}
                    disabled={alertsPage === 1}
                    className={`px-2 py-1 rounded text-xs disabled:opacity-30 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setAlertsPage(p => Math.min(totalPages, p + 1))}
                    disabled={alertsPage === totalPages}
                    className={`px-2 py-1 rounded text-xs disabled:opacity-30 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Operational Metrics (extracted component) */}
      <AitPortfolioOperationalMetrics
        opsMetrics={opsMetrics}
        opsLoading={opsLoading}
        opsMonths={opsMonths}
        setOpsMonths={setOpsMonths}
        summary={summary}
      />

      {/* Compliance Calendar — always show */}
      {opsMetrics && (() => {
        const events = opsMetrics.upcoming_events || [];
        const evtCounts = { move_in: 0, vacancy: 0, lease_expiry: 0, recert: 0 };
        events.forEach(e => { if (evtCounts[e.type] !== undefined) evtCounts[e.type]++; });
        const filtered = portEventFilter === 'all' ? events : events.filter(e => e.type === portEventFilter);
        return (
        <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar size={14} className="text-blue-500" />
              Compliance Calendar
            </h3>
          </div>

          {/* Filter toggles */}
          <div className={`flex gap-2 px-4 py-2.5 border-b ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`}>
            {[
              { key: 'all', label: 'All', count: events.length, cls: 'bg-gray-500/10 text-gray-500' },
              { key: 'move_in', label: 'Move-Ins', count: evtCounts.move_in, cls: 'bg-blue-500/10 text-blue-500' },
              { key: 'vacancy', label: 'Vacancies', count: evtCounts.vacancy, cls: 'bg-orange-500/10 text-orange-500' },
              { key: 'lease_expiry', label: 'Lease Exp', count: evtCounts.lease_expiry, cls: 'bg-orange-500/10 text-orange-500' },
              { key: 'recert', label: 'Recerts', count: evtCounts.recert, cls: 'bg-red-500/10 text-red-500' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setPortEventFilter(f.key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  portEventFilter === f.key
                    ? `${f.cls} ring-2 ring-current ring-offset-1 ${isDark ? 'ring-offset-gray-800' : 'ring-offset-white'}`
                    : `${f.cls} opacity-60 hover:opacity-100`
                }`}
              >
                {f.label} <span className="font-bold">{f.count}</span>
              </button>
            ))}
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
            {filtered.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Calendar size={24} className="mx-auto mb-2 opacity-30" />
                <p className="text-xs">{events.length === 0 ? 'No upcoming events. Lease expirations, recerts, and status transitions will appear here.' : 'No events match this filter.'}</p>
              </div>
            ) : (
            <table className="w-full text-xs">
              <thead className={`sticky top-0 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Unit</th>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Property</th>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Designation</th>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                  <th className={`text-left px-3 py-2 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Detail</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filtered.map((evt, i) => {
                  const typeStyles = {
                    move_in: { label: 'Move-In', cls: 'bg-blue-500/10 text-blue-500' },
                    vacancy: { label: 'Vacancy', cls: 'bg-orange-500/10 text-orange-500' },
                    lease_expiry: { label: 'Lease Exp', cls: 'bg-orange-500/10 text-orange-500' },
                    recert: { label: 'Recert', cls: 'bg-red-500/10 text-red-500' },
                  };
                  const style = typeStyles[evt.type] || typeStyles.recert;
                  return (
                    <tr key={i} className={`cursor-pointer ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`} onClick={() => evt.property_id && navigate(`/dashboard/ait/${evt.property_id}`)}>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${style.cls}`}>{style.label}</span>
                      </td>
                      <td className="px-3 py-2 font-medium">{evt.unit}</td>
                      <td className="px-3 py-2 truncate max-w-[120px]">{evt.property}</td>
                      <td className="px-3 py-2">{evt.designation ? `${evt.designation}%` : '—'}</td>
                      <td className="px-3 py-2">{evt.date || '—'}</td>
                      <td className={`px-3 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{evt.detail}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
        </div>
        );
      })()}

      {/* Property Cards Grid */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Properties</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map(card => {
            const status = COMPLIANCE_COLORS[card.compliance_status] || COMPLIANCE_COLORS.no_data;
            const StatusIcon = status.icon;
            return (
              <button
                key={card.id}
                onClick={() => navigate(`/dashboard/ait/${card.id}`)}
                className={`text-left p-4 rounded-xl border transition-all hover:shadow-md ${
                  isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-sm truncate pr-2 text-gray-900 dark:text-white">{card.name}</h4>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full shrink-0 ${status.bg} ${status.text}`}>
                    <StatusIcon size={10} />
                    {STATUS_LABELS[card.compliance_status] || 'No Data'}
                  </span>
                </div>
                {(card.city || card.state) && (
                  <div className={`flex items-center gap-1 text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin size={10} />
                    <span className="truncate">{[card.city, card.state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Avg Income</span>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {card.average_income != null ? `${card.average_income}%` : '-'}
                    </div>
                  </div>
                  <div>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Headroom</span>
                    <div className={`font-medium ${card.headroom != null && card.headroom < 2 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                      {card.headroom != null ? `${card.headroom}%` : '-'}
                    </div>
                  </div>
                  <div>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Flexibility</span>
                    <div className={`font-medium capitalize ${FLEXIBILITY_COLORS[card.flexibility_index] || ''}`}>
                      {card.flexibility_index || '-'}
                    </div>
                  </div>
                  <div>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Pressure</span>
                    <div className={`font-medium capitalize ${PRESSURE_COLORS[card.pressure_level] || ''}`}>
                      {card.pressure_level || '-'}
                    </div>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-2 text-xs">
                  <span className={`min-w-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {card.building_count} bldg · {card.unit_count} units · {card.occupancy_percentage || 0}% occ
                    {(card.leased || 0) > 0 && <> · <span className="text-blue-500">{card.leased} leased</span></>}
                    {(card.notice || 0) > 0 && <> · <span className="text-orange-500">{card.notice} notice</span></>}
                  </span>
                  <span className={`flex-shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded text-xs capitalize ${
                    card.user_role === 'owner'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      : card.user_role === 'editor'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {card.user_role || 'viewer'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {cards.length === 0 && (
          <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Building2 size={32} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No properties included. Add properties or adjust your filter.
            </p>
          </div>
        )}
      </div>

      

      {/* Filter Modal */}
      {showFilter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-5 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Filter Properties</h3>
              <button onClick={() => setShowFilter(false)}>
                <X size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              <button
                onClick={async () => {
                  for (const p of properties) {
                    await updatePortfolioPreference(p.id, false);
                  }
                  refreshAll();
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Include All
              </button>
              <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>|</span>
              <button
                onClick={async () => {
                  for (const p of properties) {
                    await updatePortfolioPreference(p.id, true);
                  }
                  refreshAll();
                }}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                Exclude All
              </button>
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {properties.map(p => {
                const included = includedIds.has(p.id);
                const pStatus = COMPLIANCE_COLORS[p.compliance_status] || COMPLIANCE_COLORS.no_data;
                return (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-sm truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{p.name}</span>
                      <span className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded-full shrink-0 ${pStatus.bg} ${pStatus.text}`}>
                        {STATUS_LABELS[p.compliance_status] || 'No Data'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleToggleExclusion(p.id, included)}
                      className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ml-2 ${
                        included ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform"
                        style={{ transform: included ? 'translateX(16px)' : 'translateX(0)' }}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowFilter(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
